import { Texture } from "@babylonjs/core/Materials/Textures/texture";
import { CubeTexture } from "@babylonjs/core/Materials/Textures/cubeTexture";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";
import { VertexBuffer } from "@babylonjs/core/Buffers/buffer";
import tinycolor from "tinycolor2";
import { BaseURL, GetHTMLColour, shadeColor, getItemFromArray } from "../../../components/Common";
import * as APP from "../../../redux_store/appState";
import { BASIC_SPECS_SECTIONS } from "../../../redux_store/jobState_Schemas";
import { GetSpecAllAreas } from "../PaperFunctionHelper";
import {
    GetRoofColoursAreasHTML,
    GetRoofColoursUndersideAreasHTML,
    GetSpecAllAreasColourHTML,
    GetSpecAllAreasAlpha,
} from "../PaperFunctions";

const MATERIAL_ALPHATESTANDBLEND = 3;
const CUBIC_MODE = 3;

export function updateColours(sceneMgr) {
    const { scene } = sceneMgr;
    const prev = scene.blockMaterialDirtyMechanism;
    scene.blockMaterialDirtyMechanism = true;

    try {
        const tagCache = new Map();
        const get = (tag) => {
            if (!tagCache.has(tag)) tagCache.set(tag, scene.getMeshesByTags(tag));
            return tagCache.get(tag);
        };

        const setMeshAppearance = makeSetMeshAppearance(scene);
        const colourGroup = makeColourGroup(setMeshAppearance);

        const d3 = window.app?.designer?.designer3D ?? {};

        const roofMeshMode = d3[APP.ROOFSHEET__MESH_MODE];
        const attachmentsMeshMode = d3[APP.ATTACHMENT_MESH_MODE];
        const beamsPostsMeshMode = d3[APP.BEAM_AND_POST_MESH_MODE];
        const guttersMeshMode = d3[APP.GUTTERS_AND_FLASHING_MESH_MODE];
        const footingMeshMode = d3[APP.FOOTINGS_MESH_MODE];

        const floorMesh = sceneMgr.groundMeshes.find((g) => g.name === "modelFloor");
        const showEnvironment = sceneMgr.showEnvironment;

        updateRoofSheets({ get, scene, colourGroup, setMeshAppearance, meshMode: roofMeshMode });
        updateGutters({ get, colourGroup, meshMode: guttersMeshMode });
        updateBeamsAndPosts({ get, colourGroup, setMeshAppearance, meshMode: beamsPostsMeshMode });
        updateAttachments({ get, colourGroup, meshMode: attachmentsMeshMode });
        updateFootings({ get, meshMode: footingMeshMode });
        updateInfills({ get, scene, colourGroup, setMeshAppearance });
        updateSlats({ get, colourGroup, setMeshAppearance });

        if (floorMesh) {
            updateFloor({ floorMesh, isVisible: showEnvironment, setMeshAppearance, meshMode: attachmentsMeshMode });
        }

        colourGroup({ meshes: get("brace"), colour: "#bbbbbb", area: -1 });
        colourGroup({
            meshes: get("riser_bracket"),
            area: -1,
            colour: GetHTMLColour("Galvanised")[0],
            emissive: shadeColor(GetHTMLColour("Galvanised")[0], -90),
        });
    } finally {
        scene.blockMaterialDirtyMechanism = prev;
        scene.markAllMaterialsAsDirty(1);
    }
}

function makeSetMeshAppearance(scene) {
    const CREATED_BY_KEY = "__createdBy_updateColours";

    return function setMeshAppearance(
        mesh,
        {
            colour = null,
            alpha = null,
            texture = null,
            u = 1,
            v = 1,
            emissive = null,
            textureHasAlpha = false,
            forceShow = true,
        }
    ) {
        mesh.isVisible = forceShow ? true : mesh.isVisible;
        mesh.isPickable = true;
        mesh.disableEdgesRendering();

        const mat = mesh.material;
        if (!mat) return;

        if (colour) {
            const hex = tinycolor(colour).toHexString();
            mat.ambientColor = mat.diffuseColor = Color3.FromHexString(hex);
            if (alpha == null) alpha = tinycolor(colour).getAlpha();
        }

        if (alpha != null) {
            mat.alpha = alpha;
            mat.metadata = mat.metadata || {};
            mat.metadata.last_alpha = alpha;
        }

        mat.useAlphaFromDiffuseTexture = false;
        if (mat.diffuseTexture) mat.diffuseTexture.hasAlpha = false;

        if (texture === null) {
            if (mat.diffuseTexture?.[CREATED_BY_KEY]) mat.diffuseTexture.dispose();
            if (mat.ambientTexture?.[CREATED_BY_KEY]) mat.ambientTexture.dispose();
            mat.diffuseTexture = null;
            mat.ambientTexture = null;
        }

        if (texture) {
            if (mat.diffuseTexture?.[CREATED_BY_KEY]) mat.diffuseTexture.dispose();
            if (mat.ambientTexture?.[CREATED_BY_KEY]) mat.ambientTexture.dispose();

            const tex = new Texture(BaseURL(`models/textures/${texture}`), scene);
            tex.uScale = u;
            tex.vScale = v;
            tex[CREATED_BY_KEY] = true;

            mat.ambientTexture = mat.diffuseTexture = tex;

            if (textureHasAlpha) {
                mat.diffuseTexture.hasAlpha = true;
                mat.transparencyMode = MATERIAL_ALPHATESTANDBLEND;
                mat.useAlphaFromDiffuseTexture = true;
                mat.alphaCutOff = 0.01;
            }
        }

        if (emissive) {
            const eHex = tinycolor(emissive).toHexString();
            mat.emissiveColor = mat.specularColor = Color3.FromHexString(eHex);
        }
    };
}

function makeColourGroup(setMeshAppearance) {
    return function colourGroup({
        meshes,
        area,
        colour,
        alpha = 1,
        texture = null,
        u = 1,
        v = 1,
        emissive = null,
        texAlpha = false,
        forceShow = true,
    }) {
        if (!meshes) return;
        for (const m of meshes) {
            if (area === -1 || m.data?.areaIndex === area) {
                setMeshAppearance(m, {
                    colour,
                    alpha,
                    texture,
                    u,
                    v,
                    emissive,
                    textureHasAlpha: texAlpha,
                    forceShow,
                });
            }
        }
    };
}

function updateRoofSheets({ get, scene, colourGroup, setMeshAppearance, meshMode = "Render" }) {
    const sheetMeshes = [...get("sheet"), ...get("skylight")];

    if (meshMode !== "Render") {
        for (const mesh of sheetMeshes) {
            if (meshMode === "Mesh") {
                renderEdge(mesh);
            } else {
                mesh.isVisible = false;
            }
        }

        return;
    }

    const underside = get("underside");
    const roofColour = GetRoofColoursAreasHTML();
    const roofUndColour = GetRoofColoursUndersideAreasHTML();
    const roofAlphas = GetSpecAllAreasAlpha(BASIC_SPECS_SECTIONS.ROOF_SHEETING, "RoofSheetColour", "Clear");
    const roofGroup = GetSpecAllAreas(BASIC_SPECS_SECTIONS.ROOF_SHEETING, "RoofGroupType", "");
    const roofData = GetSpecAllAreas(BASIC_SPECS_SECTIONS.ROOF_SHEETING, "RoofSheetData", "Clear");

    for (let i = 0; i < roofColour.length; i++) {
        const data = roofData[i];
        const alpha = roofAlphas[i];

        colourGroup({
            meshes: sheetMeshes,
            area: i,
            colour: roofColour[i],
            alpha,
            texture: data.texture_filename,
        });
        ["back_channel", "sheet_end", "sheet_side"].forEach((tag) =>
            colourGroup({ meshes: get(tag), area: i, colour: roofColour[i] })
        );
        colourGroup({
            meshes: underside,
            area: i,
            colour: roofUndColour[i],
            alpha,
            texture: data.bottomside_texture_filename ?? data.texture_filename,
        });
    }

    for (const mesh of underside) {
        const idx = mesh.data?.areaIndex;
        mesh.isVisible = roofGroup[idx] !== "Polycarbonate";

        const n = mesh.getVerticesData(VertexBuffer.NormalKind);
        if (n && n[2] < 0) {
            const flipped = n.map((v) => -v);
            mesh.setVerticesData(VertexBuffer.NormalKind, flipped);
            mesh.updateVerticesData(VertexBuffer.NormalKind, flipped);
        }
    }

    for (const mesh of sheetMeshes) {
        mesh.data.is_next_to_skylight = false;

        const idx = mesh.data?.areaIndex;
        const alpha = roofAlphas[idx];

        if (roofGroup[idx] === "Polycarbonate" && alpha < 0.9) {
            const texName = roofData[idx].texture_filename;

            setMeshAppearance(mesh, {
                texture: texName,
            });
            Object.assign(mesh.material, {
                transparencyMode: MATERIAL_ALPHATESTANDBLEND,
                useAlphaFromDiffuseTexture: true,
                alphaCutOff: 0.01,
            });
            mesh.material.diffuseTexture.hasAlpha = true;
        }

        const n = mesh.getVerticesData(VertexBuffer.NormalKind);
        if (n && n[2] < 0) {
            const flipped = n.map((v) => -v);
            mesh.setVerticesData(VertexBuffer.NormalKind, flipped);
            mesh.updateVerticesData(VertexBuffer.NormalKind, flipped);
        }
    }

    skylightPass({
        get,
        scene,
        sheetMeshes,
        roofData,
        colourGroup,
        setMeshAppearance,
    });
}

function updateGutters({ get, colourGroup, meshMode = "Render" }) {
    if (meshMode !== "Render") {
        for (const tag of ["gutter", "gutter_face", "downpipe"]) {
            for (const mesh of get(tag)) {
                if (meshMode === "Mesh") {
                    renderEdge(mesh);
                } else {
                    mesh.isVisible = false;
                }
            }
        }
        return;
    }

    const ridgeCol = GetSpecAllAreasColourHTML(BASIC_SPECS_SECTIONS.ROOF_SHEETING, "RidgeColour", "Opal");
    ridgeCol.forEach((c, i) => colourGroup({ meshes: get("ridge"), area: i, colour: c }));

    const bargeCol = GetSpecAllAreasColourHTML(BASIC_SPECS_SECTIONS.ROOF_SHEETING, "FlashingColour", "Opal");
    bargeCol.forEach((c, i) => colourGroup({ meshes: get("barge_cap"), area: i, colour: c }));

    const gutCol = GetSpecAllAreasColourHTML(BASIC_SPECS_SECTIONS.GUTTERS_AND_DOWNPIPES, "GutterCol", "Classic Cream");
    const gutTyp = GetSpecAllAreas(BASIC_SPECS_SECTIONS.GUTTERS_AND_DOWNPIPES, "GutterType", "Fascia Gutter");

    gutCol.forEach((c, i) => {
        colourGroup({
            meshes: get("gutter"),
            area: i,
            colour: c,
            emissive: shadeColor(c, -90),
        });

        const file =
            gutTyp[i] === "Fascia Gutter"
                ? "gutter_fasciagutter.jpg"
                : gutTyp[i].includes("OG")
                ? "gutter_og.jpg"
                : gutTyp[i] === "Quad Gutter"
                ? "gutter_quad.jpg"
                : gutTyp[i] === "Urbis Gutter"
                ? null
                : "gutter_D.jpg";

        colourGroup({
            meshes: get("gutter_face"),
            area: i,
            colour: c,
            texture: file,
            emissive: shadeColor(c, -90),
        });
    });

    const downCol = GetSpecAllAreasColourHTML(
        BASIC_SPECS_SECTIONS.GUTTERS_AND_DOWNPIPES,
        "DownpipeCol",
        "Classic Cream"
    );
    downCol.forEach((c, i) =>
        colourGroup({
            meshes: get("downpipe"),
            area: i,
            colour: c,
            emissive: shadeColor(c, -90),
        })
    );
}

function updateBeamsAndPosts({ get, colourGroup, setMeshAppearance, meshMode = "Render" }) {
    if (meshMode !== "Render") {
        for (const tag of ["beam", "beam2", "beam3", "downlight_flashing", "flashing", "post"]) {
            for (const mesh of get(tag)) {
                if (meshMode === "Mesh") {
                    renderEdge(mesh);
                } else {
                    mesh.isVisible = false;
                }
            }
        }
        return;
    }

    const paint = (tag, section, key = "Classic Cream") => {
        GetSpecAllAreasColourHTML(section, key, "Classic Cream").forEach((c, i) =>
            colourGroup({
                meshes: get(tag),
                area: i,
                colour: c,
                emissive: shadeColor(c, -90),
            })
        );
    };
    paint("beam", BASIC_SPECS_SECTIONS.BEAMS_AND_POSTS, "BeamCol1");
    paint("beam2", BASIC_SPECS_SECTIONS.BEAMS_AND_POSTS, "BeamCol2");
    paint("beam3", BASIC_SPECS_SECTIONS.BEAMS_AND_POSTS, "BeamCol3");

    GetSpecAllAreasColourHTML(BASIC_SPECS_SECTIONS.BEAMS_AND_POSTS, "BeamCol1", "Classic Cream").forEach((c, i) => {
        ["downlight_flashing", "flashing"].forEach((tag) =>
            colourGroup({
                meshes: get(tag),
                area: i,
                colour: c,
                emissive: shadeColor(c, -90),
            })
        );
    });

    for (const m of get("beam"))
        if (m.data?.beam_name?.startsWith?.("U") && m.data.colour)
            setMeshAppearance(m, {
                colour: GetHTMLColour(m.data.colour)[0],
                emissive: shadeColor(GetHTMLColour(m.data.colour)[0], -90),
            });

    const mods = GetSpecAllAreas(BASIC_SPECS_SECTIONS.BEAMSPECMODIFICATIONS, null, []);
    mods.forEach((areaMods, area) => {
        for (const m of get("beam")) {
            let d = getItemFromArray(areaMods, "Ref", m.data?.ref, "Ref2", m.data?.ref2);
            if (!d && m.data?.ref2?.startsWith("Portal2")) {
                d = getItemFromArray(areaMods, "Ref", m.data?.ref, "Ref2", m.data?.ref2.replace("Portal2", "Portal"));
            }
            if (d?.Colour && d.Area === m.data.areaIndex)
                setMeshAppearance(m, {
                    colour: GetHTMLColour(d.Colour)[0],
                    emissive: shadeColor(GetHTMLColour(d.Colour)[0], -90),
                });
        }
    });

    const postColour = GetSpecAllAreasColourHTML(BASIC_SPECS_SECTIONS.BEAMS_AND_POSTS, "PostCol", "Classic Cream");
    const postType = GetSpecAllAreas(BASIC_SPECS_SECTIONS.BEAMS_AND_POSTS, "PostType", "Classic Cream");

    postColour.forEach((c, i) => {
        const tex = window.LYSAGHT || postType[i].includes("P/C") ? "post_rhs.jpg" : null;
        colourGroup({
            meshes: get("post"),
            area: i,
            colour: c,
            texture: tex,
            emissive: shadeColor(c, -90),
        });
    });

    const pmods = GetSpecAllAreas(BASIC_SPECS_SECTIONS.POSTMODIFICATIONS, null, []);
    pmods.forEach((areaMods) => {
        for (const m of get("post")) {
            const d = getItemFromArray(areaMods, "Ref", m.data?.ref, "Ref2", m.data?.ref2);
            if (d?.Colour)
                setMeshAppearance(m, {
                    colour: GetHTMLColour(d.Colour)[0],
                    emissive: shadeColor(GetHTMLColour(d.Colour)[0], -90),
                });
        }
    });
}

function updateAttachments({ get, colourGroup, meshMode = "Render" }) {
    const attachmentRoofMeshes = get("attachment_roof");
    const attachmentMeshes = get("attachment");
    const attachmentFasciaMeshes = get("attachment_fascia");

    if (meshMode !== "Render") {
        for (const mesh of [...attachmentRoofMeshes, ...attachmentMeshes, ...attachmentFasciaMeshes]) {
            if (meshMode === "Mesh") {
                renderEdge(mesh);
            } else {
                mesh.isVisible = false;
            }
        }

        return;
    }

    const roofCol = window.job?.SiteParameters?.ExistingStructure?.RoofColour;
    if (roofCol)
        colourGroup({
            meshes: attachmentRoofMeshes,
            area: -1,
            colour: GetHTMLColour(roofCol)[0],
            texture:
                window.job?.SiteParameters?.ExistingStructure?.RoofType === "Tile" ? "qdrooftile.jpg" : "corro.jpg",
        });

    const wallCol = window.job?.SiteParameters?.ExistingStructure?.WallColour;
    const wallType = window.job?.SiteParameters?.ExistingStructure?.WallType;

    if (wallType) {
        const map = {
            Blank: ["blank.png", 1, 1],
            "Red Brick": ["red_brick.png", 2, 2],
            Rendered: ["qdrender.jpg", 4, 4],
            Stone: ["stone.jpg", 2, 2],
            Timber: ["timber.jpg", 0.5, 3],
            Blocks: ["blocks.jpg", 4, 6],
            default: ["brick_blonde.jpg", 1.3, 1.3],
        };
        const [tex, u, v] = map[wallType] ?? map.default;

        colourGroup({
            meshes: attachmentMeshes,
            area: -1,
            colour: wallCol ? GetHTMLColour(wallCol)[0] : null,
            emissive: wallCol ? shadeColor(wallCol, -70) : null,
            texture: tex,
            u,
            v,
        });
    }

    colourGroup({
        meshes: attachmentFasciaMeshes,
        area: -1,
        colour: "#eaeaea",
    });
}

function updateFloor({ floorMesh, isVisible, setMeshAppearance, meshMode = "Render" }) {
    if (meshMode !== "Render") {
        if (meshMode === "Mesh") {
            renderEdge(floorMesh);
        } else {
            floorMesh.isVisible = false;
        }

        return;
    }

    floorMesh.isVisible = isVisible;

    const floorCol = window.job?.SiteParameters?.ExistingStructure?.FloorColour;
    const floorType = window.job?.SiteParameters?.ExistingStructure?.FloorType;

    if (floorType) {
        const map = {
            Blank: ["blank.png", 1, 1],
            Timber: ["floor_decking.jpg", 2, 2],
            Sandstone: ["floor_sandstone.jpg", 2, 2],
            Concrete: ["concrete.png", 12, 12],
            default: ["floor_paving1.jpg", 3, 3],
        };
        const [tex, u, v] = map[floorType] ?? map.default;

        setMeshAppearance(floorMesh, {
            colour: floorCol ? GetHTMLColour(floorCol)[0] : null,
            emmissive: floorCol ? shadeColor(floorCol, -70) : null,
            texture: tex,
            u,
            v,
            forceShow: false,
        });
    }
}

function updateInfills({ get, scene, colourGroup, setMeshAppearance }) {
    const run = (tag, colourKey, dispKey, alphaKey, texKey) => {
        const colour = GetSpecAllAreasColourHTML(BASIC_SPECS_SECTIONS.GABLE_PARAMETERS, colourKey, "Plain");
        const disp = GetSpecAllAreasColourHTML(BASIC_SPECS_SECTIONS.GABLE_PARAMETERS, dispKey, null);
        const alpha = GetSpecAllAreasAlpha(BASIC_SPECS_SECTIONS.GABLE_PARAMETERS, colourKey, "Clear");
        const tex = GetSpecAllAreas(BASIC_SPECS_SECTIONS.GABLE_PARAMETERS, texKey, null);

        colour.forEach((c, i) => {
            const col = disp[i] ?? c;
            if (tex[i])
                colourGroup({
                    meshes: get(tag),
                    area: i,
                    colour: col,
                    alpha: alpha[i],
                    texture: tex[i].filename,
                    emissive: shadeColor(col, -60),
                    texAlpha: tex[i].has_alpha,
                });
            else
                colourGroup({
                    meshes: get(tag),
                    area: i,
                    colour: col,
                    alpha: alpha[i],
                    emissive: shadeColor(col, -60),
                });
        });

        GetSpecAllAreasColourHTML(BASIC_SPECS_SECTIONS.GABLE_PARAMETERS, colourKey, null, true).forEach((c, i) =>
            colourGroup({
                meshes: get(`${tag}_underside`),
                area: i,
                colour: disp[i] ?? c,
                alpha: alpha[i] === 1 ? 1 : 0.01,
                emissive: shadeColor(disp[i] ?? c, -60),
            })
        );

        const meshes = get(tag);
        meshes.forEach((m) => {
            const idx = m.data?.areaIndex;
            if (idx == null || !tex[idx]) return;
            const t = tex[idx];
            const uv = m.getVerticesData("uv");
            const x = uv[0] || uv[2] || 1;
            const s = t.scale_mm / x;
            ["ambientTexture", "diffuseTexture"].forEach((k) => {
                if (m.material[k]) {
                    m.material[k].uScale = s;
                    m.material[k].vScale = s;
                }
            });
            m.material.twoSidedLighting = true;

            if (t.reflective) {
                m.material.reflectionTexture = new CubeTexture(BaseURL("textures/test/TropicalSunnyDay"), scene);
                m.material.reflectionTexture.coordinatesMode = CUBIC_MODE;
                m.material.reflectionTexture.level = t.reflective;
            } else {
                m.material.reflectionTexture = null;
            }
        });

        const val = GetSpecAllAreas(
            BASIC_SPECS_SECTIONS.GABLE_PARAMETERS,
            tag === "infill_1" ? "TrussInfill1" : "TrussInfill2",
            "None"
        );
        val.forEach((v, i) => toggleVisible(get(tag), i, v !== "None"));

        const beamFlag = GetSpecAllAreas(
            BASIC_SPECS_SECTIONS.GABLE_PARAMETERS,
            tag === "infill_1" ? "HasInfillBeam1" : "HasInfillBeam2",
            1
        );
        beamFlag.forEach((v, i) => toggleVisible(get(`${tag}_beam`), i, v !== 0));
    };

    run("infill_1", "TrussInfillColour1", "TrussInfillDisplayColour1", "TrussInfillColour1", "TrussInfillTexture1");
    run("infill_2", "TrussInfillColour2", "TrussInfillDisplayColour2", "TrussInfillColour2", "TrussInfillTexture2");
}

function updateSlats({ get, colourGroup, setMeshAppearance }) {
    const areas = GetSpecAllAreas("SlatPanels", null, []);
    if (!areas.length) return;

    areas.forEach((mods, idx) => {
        const defaultCol = mods?.[0]?.Colour;
        const apply = (tag, f1, f2) => {
            for (const m of get(tag)) {
                const d = getItemFromArray(mods, f1, m.data?.ref, f2, m.data?.ref5 ?? null);
                const col = d?.Colour
                    ? GetHTMLColour(d.Colour)[0]
                    : m.data.areaIndex === idx && defaultCol
                    ? GetHTMLColour(defaultCol)[0]
                    : null;
                if (col)
                    setMeshAppearance(m, {
                        colour: col,
                        emissive: shadeColor(col, -90),
                    });
            }
        };
        apply("slat_panel", "Ref", "Ref2");
        apply("slat_post", "P1", "P2");
    });
}

function updateFootings({ get, meshMode = "Render" }) {
    const footingMeshes = get("post_footing");

    for (const mesh of footingMeshes) {
        if (meshMode === "Render") {
            mesh.isVisible = true;
            mesh.isPickable = true;
            mesh.disableEdgesRendering();

            const mat = mesh.material;
            if (mat) {
                mat.alpha = 1.0;
            }
        } else if (meshMode === "Mesh") {
            renderEdge(mesh);
        } else {
            mesh.isVisible = false;
        }
    }
}

function skylightPass({ get, scene, sheetMeshes, roofData, colourGroup, setMeshAppearance }) {
    const skyColour = GetSpecAllAreasColourHTML(BASIC_SPECS_SECTIONS.ROOF_SHEETING, "SkylightColour", "Opal");
    const skyAlpha = GetSpecAllAreasAlpha(BASIC_SPECS_SECTIONS.ROOF_SHEETING, "SkylightColour", "Clear");

    const skylights = sheetMeshes.filter((m) => m.data.is_skylight);
    const skylightEnds = get("skylight_end");
    const skylightSides = get("skylight_side");

    let diffWidth = false;

    for (let i = 0; i < skyColour.length; i++) {
        colourGroup({
            meshes: skylights,
            area: i,
            colour: skyColour[i],
            alpha: skyAlpha[i],
            texture: roofData[i].skylight_texture_filename,
        });
        colourGroup({
            meshes: skylightEnds,
            area: i,
            colour: skyColour[i],
            alpha: skyAlpha[i],
        });
        colourGroup({
            meshes: skylightSides,
            area: i,
            colour: skyColour[i],
            alpha: skyAlpha[i],
        });

        for (const mesh of skylights) {
            if (mesh.data.areaIndex !== i) continue;
            if (mesh.data?.is_underside) {
                mesh.isVisible = false;
                continue;
            }

            const data = roofData[i];
            if (data.skylight_coverage && data.skylight_coverage !== data.coverage) diffWidth = true;

            if (skyAlpha[i] < 0.9) {
                setMeshAppearance(mesh, { texture: data.skylight_texture_filename });
                Object.assign(mesh.material, {
                    transparencyMode: MATERIAL_ALPHATESTANDBLEND,
                    useAlphaFromDiffuseTexture: true,
                    alphaCutOff: 0.01,
                });
            }

            if (data.skylight_spacing > 0) {
                const [sec, offset] = (mesh.data.ref2 ?? "").split("/").map(Number);
                for (const sheet of sheetMeshes) {
                    const [s2, off2] = (sheet.data.ref2 ?? "").split("/").map(Number);
                    if (sec === s2 && Math.abs(offset - off2) <= data.skylight_spacing) {
                        sheet.data.is_next_to_skylight = true;
                    }
                }
            }
        }
    }

    if (!diffWidth) updateSkylights3D({ get, scene });
}

function updateSkylights3D({ get, scene }) {
    const skylightsAll = GetSpecAllAreas("SKYLIGHTS", null, []);
    const objs = [...get("skylight"), ...get("sheet")];
    if (!objs.length) return false;

    objs.forEach((m) => m.data && (m.data.is_skylight = false));

    skylightsAll.forEach((area_skylights, areaIndex) => {
        area_skylights.forEach((skylight) => {
            const ref = `${skylight.Section}/${skylight.Offset}`;
            const ref2 =
                Number.isFinite(+skylight?.Index) && +skylight.Index >= 0
                    ? `${skylight.Section}/${skylight.Index}`
                    : null;

            for (const mesh of objs) {
                if (!mesh.data || mesh.data.areaIndex !== areaIndex) continue;
                if (ref2 && mesh.data?.ref2) {
                    if (ref2 === mesh.data.ref2) mesh.data.is_skylight = true;
                } else if (ref === mesh.data?.ref) {
                    mesh.data.is_skylight = true;
                }
            }
        });
    });

    return true;
}

function toggleVisible(meshes, area, show) {
    meshes.forEach((m) => {
        if (area === -1 || m.data?.areaIndex === area) m.isVisible = show;
    });
}

function renderEdge(mesh) {
    mesh.enableEdgesRendering(0.999);
    mesh.material.alpha = 0.2;
    mesh.edgesColor = new Color4(0.0, 0.0, 0.0, 1.0);
    mesh.edgesWidth = 100;
    mesh.isPickable = false;
    mesh.isVisible = true;
}
